import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { toast } from 'sonner';
import { phoneApi, type PBXPublicConfig } from '../../lib/api';

// --- Types ---

export type CallDirection = 'inbound' | 'outbound';
export type CallState = 'idle' | 'dialing' | 'ringing' | 'active' | 'held' | 'ended';

export type ActiveCall = {
	callId: string;
	channelId?: string;
	direction: CallDirection;
	number: string;
	contactName?: string;
	contactId?: number;
	companyId?: number;
	state: CallState;
	startedAt?: Date;
	duration: number;
};

type PhoneState = {
	pbxEnabled: boolean;
	pbxHealthy: boolean;
	registered: boolean;
	activeCall: ActiveCall | null;
	recentCalls: ActiveCall[];
	dialpadOpen: boolean;
	minimized: boolean;
};

type PhoneAction =
	| { type: 'SET_PBX_CONFIG'; enabled: boolean; healthy: boolean }
	| { type: 'SET_REGISTERED'; registered: boolean }
	| { type: 'START_CALL'; call: ActiveCall }
	| { type: 'UPDATE_CALL'; updates: Partial<ActiveCall> }
	| { type: 'END_CALL' }
	| { type: 'TOGGLE_DIALPAD' }
	| { type: 'SET_MINIMIZED'; minimized: boolean };

function phoneReducer(state: PhoneState, action: PhoneAction): PhoneState {
	switch (action.type) {
		case 'SET_PBX_CONFIG':
			return { ...state, pbxEnabled: action.enabled, pbxHealthy: action.healthy };
		case 'SET_REGISTERED':
			return { ...state, registered: action.registered };
		case 'START_CALL':
			return { ...state, activeCall: action.call, dialpadOpen: false, minimized: false };
		case 'UPDATE_CALL':
			if (!state.activeCall) return state;
			return { ...state, activeCall: { ...state.activeCall, ...action.updates } };
		case 'END_CALL':
			return {
				...state,
				activeCall: null,
				recentCalls: state.activeCall
					? [{ ...state.activeCall, state: 'ended' }, ...state.recentCalls.slice(0, 19)]
					: state.recentCalls
			};
		case 'TOGGLE_DIALPAD':
			return { ...state, dialpadOpen: !state.dialpadOpen };
		case 'SET_MINIMIZED':
			return { ...state, minimized: action.minimized };
		default:
			return state;
	}
}

const initialState: PhoneState = {
	pbxEnabled: false,
	pbxHealthy: false,
	registered: false,
	activeCall: null,
	recentCalls: [],
	dialpadOpen: false,
	minimized: false
};

// --- Context ---

type PhoneContextValue = PhoneState & {
	dial: (number: string, contactId?: number, companyId?: number, contactName?: string) => Promise<void>;
	hangup: () => void;
	toggleDialpad: () => void;
	setMinimized: (minimized: boolean) => void;
};

const PhoneContext = createContext<PhoneContextValue | undefined>(undefined);

// --- Provider ---

export function PhoneProvider({ children, pbxConfig }: { children: ReactNode; pbxConfig?: PBXPublicConfig }) {
	const [state, dispatch] = useReducer(phoneReducer, initialState);
	const durationRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// Check PBX health on mount
	useEffect(() => {
		if (!pbxConfig?.enabled) {
			dispatch({ type: 'SET_PBX_CONFIG', enabled: false, healthy: false });
			return;
		}

		let cancelled = false;
		const check = async () => {
			try {
				const result = await phoneApi.health();
				if (!cancelled) {
					dispatch({ type: 'SET_PBX_CONFIG', enabled: result.enabled, healthy: result.healthy ?? false });
				}
			} catch {
				if (!cancelled) {
					dispatch({ type: 'SET_PBX_CONFIG', enabled: true, healthy: false });
				}
			}
		};

		void check();
		const interval = setInterval(check, 60_000);
		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [pbxConfig?.enabled]);

	// Duration timer for active call
	useEffect(() => {
		if (state.activeCall?.state === 'active') {
			durationRef.current = setInterval(() => {
				dispatch({ type: 'UPDATE_CALL', updates: { duration: (state.activeCall?.duration ?? 0) + 1 } });
			}, 1000);
		} else if (durationRef.current) {
			clearInterval(durationRef.current);
			durationRef.current = null;
		}
		return () => {
			if (durationRef.current) clearInterval(durationRef.current);
		};
	}, [state.activeCall?.state]);

	// SSE for real-time call events
	useEffect(() => {
		if (!pbxConfig?.enabled) return;

		const token = window.localStorage.getItem('auth_token');
		const baseUrl = (import.meta.env?.VITE_API_BASE as string | undefined) || '/api';
		const url = `${baseUrl}/phone/events`;

		const eventSource = new EventSource(url);

		eventSource.onmessage = (event) => {
			try {
				const data = JSON.parse(event.data) as {
					type: string;
					call_id?: string;
					channel_id?: string;
					direction?: string;
					from_number?: string;
					to_number?: string;
					state?: string;
					cause?: string;
					contact_id?: number;
					contact_name?: string;
					company_id?: number;
					company_name?: string;
				};

				switch (data.type) {
					case 'ringing':
						if (data.direction === 'inbound') {
							const contactName = data.contact_name || undefined;
							const displayName = contactName
								? `${contactName}${data.company_name ? ` (${data.company_name})` : ''}`
								: undefined;

							dispatch({
								type: 'START_CALL',
								call: {
									callId: data.call_id ?? '',
									channelId: data.channel_id,
									direction: 'inbound',
									number: data.from_number ?? 'Unknown',
									contactName: displayName,
									contactId: data.contact_id,
									companyId: data.company_id,
									state: 'ringing',
									duration: 0
								}
							});

							// Show incoming call toast notification
							toast.info(
								contactName
									? `Incoming call from ${contactName}`
									: `Incoming call from ${data.from_number ?? 'Unknown'}`,
								{ duration: 10000 }
							);
						}
						break;
					case 'answered':
						dispatch({
							type: 'UPDATE_CALL',
							updates: { state: 'active', startedAt: new Date() }
						});
						break;
					case 'hangup':
					case 'completed':
						dispatch({ type: 'END_CALL' });
						break;
				}
			} catch {
				// Ignore parse errors
			}
		};

		eventSource.onerror = () => {
			// SSE will auto-reconnect
		};

		return () => eventSource.close();
	}, [pbxConfig?.enabled]);

	const dial = useCallback(
		async (number: string, contactId?: number, companyId?: number, contactName?: string) => {
			if (state.activeCall) {
				toast.error('A call is already in progress');
				return;
			}

			// Start local call state immediately for responsiveness
			const tempCallId = `local-${Date.now()}`;
			dispatch({
				type: 'START_CALL',
				call: {
					callId: tempCallId,
					direction: 'outbound',
					number,
					contactName,
					contactId,
					companyId,
					state: 'dialing',
					duration: 0
				}
			});

			try {
				const result = await phoneApi.originate({ to_number: number, contact_id: contactId, company_id: companyId });
				dispatch({
					type: 'UPDATE_CALL',
					updates: {
						callId: String(result.call?.call_id ?? result.channel_id),
						channelId: result.channel_id,
						state: 'ringing'
					}
				});
			} catch (error) {
				toast.error(error instanceof Error ? error.message : 'Failed to place call');
				dispatch({ type: 'END_CALL' });
			}
		},
		[state.activeCall]
	);

	const hangup = useCallback(() => {
		dispatch({ type: 'END_CALL' });
		toast.info('Call ended');
	}, []);

	const toggleDialpad = useCallback(() => {
		dispatch({ type: 'TOGGLE_DIALPAD' });
	}, []);

	const setMinimized = useCallback((minimized: boolean) => {
		dispatch({ type: 'SET_MINIMIZED', minimized });
	}, []);

	const value = useMemo(
		() => ({ ...state, dial, hangup, toggleDialpad, setMinimized }),
		[state, dial, hangup, toggleDialpad, setMinimized]
	);

	return <PhoneContext.Provider value={value}>{children}</PhoneContext.Provider>;
}

export function usePhone() {
	const context = useContext(PhoneContext);
	if (!context) {
		throw new Error('usePhone must be used within PhoneProvider');
	}
	return context;
}
